import type { ReactNode } from 'react'
import { ApiError } from '../api/client'

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading">
      <span className="spinner" /> <span style={{ marginLeft: 8 }}>{label}</span>
    </div>
  )
}

export function Empty({ children = 'Nothing to show for this period.' }: { children?: ReactNode }) {
  return <div className="empty">{children}</div>
}

/**
 * One place that turns a thrown error into something an admin can act on.
 * 403 gets its own wording because it is a routine outcome here — several
 * endpoints are restricted to the root admin account.
 */
export function ErrorBox({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.isForbidden) {
    return (
      <div className="alert warn">
        <strong>Not available for this account.</strong> {error.detail}
      </div>
    )
  }
  const message =
    error instanceof Error ? error.message : 'Something went wrong loading this data.'
  return (
    <div className="alert error">
      <strong>Could not load.</strong> {message}
    </div>
  )
}

/** Wraps the three states every panel has, so pages stay readable. */
export function Panel({
  isLoading,
  error,
  isEmpty,
  children,
  emptyText,
}: {
  isLoading: boolean
  error: unknown
  isEmpty?: boolean
  children: ReactNode
  emptyText?: string
}) {
  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (isEmpty) return <Empty>{emptyText ?? 'Nothing to show for this period.'}</Empty>
  return <>{children}</>
}
